<template>
  <div class="gi-root resizable-columns">

    <div class="gi-table-label">DRAFT INVOICES</div>

    <!-- Selection action bar -->
    <div class="gi-action-bar" v-if="modelValue.length">
      <span class="gi-action-bar__count">{{ modelValue.length }} selected</span>
      <div class="gi-action-bar__buttons">
        <b-button size="sm" variant="primary" :disabled="exportState.bulkExporting.value" @click="exportState.exportSelected(selectedDraftObjects, 'xls')">↓ XLS</b-button>
        <b-button size="sm" variant="primary" :disabled="exportState.bulkExporting.value" @click="exportState.exportSelected(selectedDraftObjects, 'pdf')">↓ PDF</b-button>
        <b-button size="sm" variant="primary" class="gi-approve-btn" :disabled="!!approving || hasMissingRequirements" @click="approveSelected">
          <b-spinner v-if="approving" small class="mr-1" />
          <b-icon v-else icon="check2" class="mr-1" />
          Approve
          <span ref="approveInfoIcon" class="gi-info-icon ml-1" @click.stop><b-icon icon="info-circle" /></span>
        </b-button>
        <b-tooltip :target="() => $refs.approveInfoIcon" placement="top" triggers="hover focus">
          {{ hasMissingRequirements ? `Blocked: missing ${missingRequirementsSummary}` : 'Approve for Auto Dispatch' }}
        </b-tooltip>
        <b-button size="sm" variant="warning" :disabled="exportState.bulkExporting.value || !!approving" @click="clearSelection">✕ Clear</b-button>
      </div>
    </div>

    <!-- Bulk export progress bar -->
    <div class="gi-progress-bar" v-if="exportState.bulkExporting.value">
      <div class="gi-progress-bar__label">
        Generating {{ exportState.bulkExportFormat.value === 'pdf' ? 'PDFs' : 'spreadsheets' }}…
        {{ exportState.bulkExportDone.value }} of {{ exportState.bulkExportTotal.value }} complete
      </div>
      <div class="gi-progress-bar__track">
        <div class="gi-progress-bar__fill" :style="{ width: exportState.bulkExportPct.value + '%' }"></div>
      </div>
    </div>

    <!-- Generating PDF/XLS after approval -->
    <div class="gi-progress-bar" v-if="exportState.docsUploading.value">
      <div class="gi-progress-bar__label">
        Generating invoice PDF/XLS… {{ exportState.docsUploadDone.value }} of {{ exportState.docsUploadTotal.value }} complete
      </div>
      <div class="gi-progress-bar__track">
        <div class="gi-progress-bar__fill" :style="{ width: (exportState.docsUploadTotal.value ? Math.round(exportState.docsUploadDone.value / exportState.docsUploadTotal.value * 100) : 0) + '%' }"></div>
      </div>
    </div>

    <div v-if="loadingDrafts" class="gi-loading">Loading pending invoices…</div>
    <div v-else-if="draftError" class="gi-error">{{ draftError }}</div>
    <div v-else-if="drafts.length === 0" class="gi-empty">No pending invoices found.</div>

    <BaseDataTable
      v-else
      v-model="modelValue"
      ref="dataTableRef"
      :columns="GenerateInvoicesTableColumns"
      :items="itemsProvider"
      :total="total"
      :initial-page-size="100"
      :page-size-options="[100, 200, 500, 1000]"
      :tbody-tr-class="tableTbodyTrClass"
      :sticky-actions-column="false"
      selectable
    >
      <!-- Left side of the table header, opposite the search box. -->
      <template v-slot:header(left)>
        <b-form-radio-group
          v-model="billingTypeModel"
          :options="billingTypeOptions"
          buttons
          button-variant="outline-primary"
          size="sm"
          class="gi-billing-toggle"
          aria-label="Filter by billing type"
        />
      </template>

      <template v-slot:cell(invoice_ref)="{ item }">
        <span class="gi-ref">{{ exportState.generateRef(item) }}</span>
      </template>

      <template v-slot:cell(billing_type)="{ item }">{{ item.billing_type || 'Market' }}</template>

      <template v-slot:cell(tpa)="{ item }">{{ item.tpa_name || '—' }}</template>
      <template v-slot:cell(underwriter)="{ item }">{{ item.underwriter_name || '—' }}</template>
      <template v-slot:cell(syndicate_number)="{ item }">{{ item.syndicate_number || '—' }}</template>
      <template v-slot:cell(currency_type)="{ item }">{{ item.currency_type }}</template>

      <template v-slot:cell(current_bdx_count)="{ item }">
        <span class="gi-num">{{ item.current_bdx_count }}</span>
      </template>

      <template v-slot:cell(previous_bdx_count)="{ item }">
        <span class="gi-num">{{ item.previous_bdx_count !== null ? item.previous_bdx_count : '—' }}</span>
      </template>

      <template v-slot:cell(bdx_variance)="{ item }">
        <span class="gi-num" :class="exportState.varianceClass(item.bdx_variance_alert)">
          <template v-if="item.bdx_variance !== null && item.bdx_variance !== undefined">
            {{ item.bdx_variance >= 0 ? '+' : '' }}{{ item.bdx_variance }}
          </template>
          <template v-else>—</template>
        </span>
      </template>

      <template v-slot:cell(amount_variance)="{ item }">
        <span class="gi-num" :class="exportState.varianceClass(item.amount_variance_alert)">
          <template v-if="item.amount_variance !== null && item.amount_variance !== undefined">
            {{ item.amount_variance >= 0 ? '+' : '' }}{{ exportState.formatCurrency(item.amount_variance, item.currency_type) }}
          </template>
          <template v-else>—</template>
        </span>
      </template>

      <template v-slot:cell(current_amount)="{ item }">
        <span class="gi-num gi-amount">{{ exportState.formatCurrency(item.current_amount, item.currency_type) }}</span>
      </template>

      <template v-slot:cell(billing_contact)="{ item }">
        <span class="gi-contact" v-if="exportState.billingContactList(item).length">
          {{ exportState.billingContactList(item)[0] }}
          <a
            v-if="exportState.billingContactList(item).length > 1"
            href
            class="gi-contact-more"
            @click.prevent="exportState.openBillingContactsModal(item)"
          >+{{ exportState.billingContactList(item).length - 1 }}</a>
        </span>
        <span v-else class="gi-alert-flag" title="TPA has no billing contact on file — this draft cannot be approved">— missing</span>
      </template>

      <template v-slot:cell(tpa_address)="{ item }">
        <span v-if="item.tpa_address" class="gi-contact gi-truncate-cell" :title="item.tpa_address">{{ firstAddressLine(item.tpa_address) }}</span>
        <span v-else-if="item.billing_type === 'LIMOSS'" class="gi-contact">—</span>
        <span v-else class="gi-alert-flag" title="TPA has no address on file — this draft cannot be approved">— missing</span>
      </template>

      <template v-slot:cell(filevine_id)="{ item }">
        <span v-if="item.tpa_filevine_id" class="gi-contact gi-truncate-cell" :title="item.tpa_filevine_id">{{ truncateFilevine(item.tpa_filevine_id) }}</span>
        <span v-else-if="item.billing_type === 'LIMOSS'" class="gi-contact">—</span>
        <span v-else class="gi-alert-flag" title="TPA has no FileVine Project Email on file — this draft cannot be approved">— missing</span>
      </template>

    </BaseDataTable>

    <!-- Billing Contacts Modal -->
    <b-modal
      v-model="exportState.showBillingContactsModal.value"
      centered
      hide-header
      hide-footer
      size="sm"
      @hide="exportState.onBillingContactsModalHide"
    >
      <div class="bcm-root">
        <button class="bcm-close" @click="exportState.onBillingContactsModalHide" aria-label="Close">
          <b-icon icon="x" />
        </button>
        <div class="bcm-header">
          <div class="bcm-header__icon-wrap">
            <b-icon icon="envelope-fill" class="bcm-header__icon" />
          </div>
          <div>
            <p class="bcm-header__title">Billing Contacts</p>
            <p class="bcm-header__sub" v-if="exportState.billingContactsModalContext.value">
              {{ exportState.billingContactsModalContext.value.tpa_name || 'TPA' }}
              <span v-if="exportState.billingContactsModalContext.value.underwriter_name">
                · {{ exportState.billingContactsModalContext.value.underwriter_name }}
              </span>
            </p>
          </div>
        </div>
        <ul class="bcm-list">
          <li v-for="(email, idx) in exportState.billingContactsModalEmails.value" :key="idx" class="bcm-list__item">
            <b-icon icon="envelope" class="bcm-list__icon" />
            {{ email }}
          </li>
        </ul>
      </div>
    </b-modal>

  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, nextTick, onUpdated, getCurrentInstance } from '@vue/composition-api'

import { BaseDataTable } from '@components/base'
import { enableColumnResize, removeColumnResize } from '@components/base/base-data-table/useResizableColumns'

import InvoicingService from '@services/InvoicingService'
import { useInvoiceExport } from './useInvoiceExport'
import { getGenerateInvoicesTableColumns, DRAFT_BILLING_TYPE_OPTIONS } from './Generateinvoicetable.constants'
import { draftMatches } from './draftSummary'

// Draft groups sent per bulk-approve request — see approveSelected().
const BULK_APPROVE_BATCH_SIZE = 3

export default defineComponent({
  name: 'PendingInvoicesTable',

  components: {
    BaseDataTable,
  },

  props: {
    // Market / LIMOSS / Both — owned by GenerateInvoicesTab (.sync) so the
    // summary cards above can follow it, and so it survives this table
    // being remounted after an approve.
    billingType: {
      type: String,
      default: 'Both',
    },
  },

  emits: ['changed', 'update:billingType', 'filtered'],

  setup(props, { emit }) {
    const instance = getCurrentInstance()
    const proxy = instance.proxy

    const exportState = useInvoiceExport()

    // Follows the Market / LIMOSS / Both toggle — see
    // getGenerateInvoicesTableColumns for which columns each one hides.
    const GenerateInvoicesTableColumns = computed(() => getGenerateInvoicesTableColumns(billingTypeFilter.value))

    const drafts = ref([])
    const loadingDrafts = ref(false)
    const draftError = ref(null)
    const total = ref(0)

    const modelValue = ref([])
    const dataTableRef = ref(null)

    // Market / LIMOSS / Both toggle in the table's top-right (LIQ-2170).
    const billingTypeOptions = DRAFT_BILLING_TYPE_OPTIONS
    const billingTypeFilter = ref(props.billingType)
    watch(() => props.billingType, (value) => { billingTypeModel.value = value })

    // The toggle changes which header cells render, so the resize handles
    // injected into them have to come out first — synchronously, before
    // Vue re-renders the header (see removeColumnResize). onUpdated below
    // puts them back once the new columns are in place.
    const billingTypeModel = computed({
      get: () => billingTypeFilter.value,
      set: (value) => {
        if (value === billingTypeFilter.value) return
        removeColumnResize(instance.proxy.$el)
        billingTypeFilter.value = value
        emit('update:billingType', value)
      },
    })
    const billingTypeOf = (draft) => draft.billing_type || 'Market'

    // Whether a bulk approve is currently in flight — always dispatch_mode
    // 'auto' now that approval is a single action (no more Manual/Auto choice).
    const approving = ref(false)

    // ── Data loading ─────────────────────────────────────────────────────

    const loadDrafts = async () => {
      loadingDrafts.value = true
      draftError.value = null
      try {
        const resp = await InvoicingService.getDraftInvoices()
        const inner = resp.data && resp.data.data
        const rows = inner && inner.data
        drafts.value = Array.isArray(rows) ? rows : []
      } catch (e) {
        draftError.value = 'Failed to load pending invoices.'
        console.error(e)
      } finally {
        loadingDrafts.value = false
      }
    }

    // A pending/draft "invoice" is just unlinked InvoiceCharge rows grouped
    // by (billing type, tpa, underwriter, syndicate, currency, month, year) —
    // Lincoln never assigns it a persisted invoice_id (that only happens on
    // approval), so BaseDataTable's `id` has to be a synthetic composite key.
    // A Market and a LIMOSS group can share every other field, and a LIMOSS
    // group splits further by syndicate (always null for Market).
    const draftKey = (draft) =>
      `${billingTypeOf(draft)}-${draft.tpa_company_id}-${draft.underwriter_company_id}-${draft.syndicate_number}-${draft.currency_type}-${draft.billing_month}-${draft.billing_year}`

    const withIds = (rows) => rows.map(d => ({ ...d, id: draftKey(d) }))

    const selectedDraftObjects = computed(() =>
      drafts.value.filter(d => modelValue.value.includes(draftKey(d))))

    // Shows only the first line of a TPA address for the Draft table's
    // Address column (LIQ-1859: "show the first line of the address") —
    // split on the first comma or newline. Full text is still available
    // via the cell's title attribute (native tooltip on hover).
    const firstAddressLine = (address) => {
      if (!address) return ''
      const firstLine = address.split(/[,\n]/)[0].trim()
      return firstLine || address.trim()
    }

    // Truncates a long FileVine value with an ellipsis for the Draft
    // table's FileVine column (LIQ-1859: e.g. "george.mccarthy@lim…").
    // The priority is flagging where it's missing, not showing the full
    // value, which stays available via the cell's title tooltip.
    const truncateFilevine = (value) => {
      if (!value) return ''
      return value.length > 18 ? `${value.slice(0, 18)}…` : value
    }

    // FileVine ID, billing contact, and address are all required on the
    // TPA's Company profile (billing contact lives on Members instead)
    // before a draft can be approved auto or manual. FileVine ID and Address
    // are TPA-only concepts — a LIMOSS draft has no TPA at all (it's grouped
    // by underwriter only), so neither applies; its billing contact is
    // always support@limeiq.com, resolved server-side (see
    // _enrich_with_billing_contacts), so that check never actually blocks it.
    const draftMissingRequirements = (draft) => {
      const missing = []
      if (draft.billing_type !== 'LIMOSS') {
        if (!draft.tpa_filevine_id) missing.push('FileVine Project Email')
        if (!draft.tpa_address) missing.push('Address')
      }
      if (!exportState.billingContactList(draft).length) missing.push('Billing Contact')
      return missing
    }

    const hasMissingRequirements = computed(() =>
      selectedDraftObjects.value.some(d => draftMissingRequirements(d).length > 0))

    const missingRequirementsSummary = computed(() => {
      const reasons = new Set()
      selectedDraftObjects.value.forEach(d => draftMissingRequirements(d).forEach(r => reasons.add(r)))
      return [...reasons].join(', ')
    })

    // ── BaseDataTable items provider ────────────────────────────────────
    // Drafts are computed client-side (no real backend pagination), so this
    // provider filters/sorts/paginates the in-memory array and returns the
    // shape BaseDataTable expects.

    const itemsProvider = async (context) => {
      const { filter, filters, currentPage, perPage, sortBy, sortDesc } = context

      const filterMap = {}
      if (filters && filters.length) {
        filters.forEach(f => { filterMap[f.key] = f.value })
      }

      // Same predicate the summary cards use (see draftSummary.js), so the
      // rows here and the figures above always agree.
      const criteria = { billingType: billingTypeFilter.value, filterMap, search: filter || '' }
      const helpers = { generateRef: exportState.generateRef, billingContactList: exportState.billingContactList }
      let rows = withIds(drafts.value).filter(d => draftMatches(d, criteria, helpers))

      // Every matching row (not just this page) — GenerateInvoicesTab
      // narrows the summary cards to these once a search/filter is in use.
      emit('filtered', { rows, filterMap, search: criteria.search })

      const sortKeyMap = {
        invoice_ref: d => exportState.generateRef(d),
        billing_type: d => billingTypeOf(d),
        tpa: d => d.tpa_name || '',
        underwriter: d => d.underwriter_name || '',
        syndicate_number: d => d.syndicate_number || '',
        currency_type: d => d.currency_type || '',
        current_bdx_count: d => d.current_bdx_count || 0,
        previous_bdx_count: d => d.previous_bdx_count === null || d.previous_bdx_count === undefined ? -Infinity : d.previous_bdx_count,
        bdx_variance: d => d.bdx_variance === null || d.bdx_variance === undefined ? -Infinity : d.bdx_variance,
        amount_variance: d => d.amount_variance === null || d.amount_variance === undefined ? -Infinity : d.amount_variance,
        current_amount: d => d.current_amount || 0,
        billing_contact: d => exportState.billingContactList(d).join(' '),
        tpa_address: d => d.tpa_address || '',
        filevine_id: d => d.tpa_filevine_id || '',
      }

      if (sortBy && sortKeyMap[sortBy]) {
        const getKey = sortKeyMap[sortBy]
        rows = [...rows].sort((a, b) => {
          const av = getKey(a)
          const bv = getKey(b)
          if (av < bv) return -1
          if (av > bv) return 1
          return 0
        })
        if (sortDesc) rows.reverse()
      }

      total.value = rows.length

      const start = (currentPage - 1) * perPage
      return rows.slice(start, start + perPage)
    }

    const handleRefresh = async () => {
      await nextTick()
      if (!dataTableRef.value) return
      dataTableRef.value.handleRefresh()
    }

    const clearSelection = () => {
      modelValue.value = []
    }

    // Switching the toggle drops any selection that's no longer visible, so
    // Approve/Export can never act on rows the user can't see.
    watch(billingTypeFilter, async () => {
      const visibleIds = new Set(withIds(drafts.value)
        .filter(d => billingTypeFilter.value === 'Both' || billingTypeOf(d) === billingTypeFilter.value)
        .map(d => d.id))
      modelValue.value = modelValue.value.filter(id => visibleIds.has(id))
      // A filter chip on a column the toggle just hid (e.g. TPA when
      // switching to LIMOSS) would keep narrowing the rows invisibly.
      const table = dataTableRef.value
      if (table && table.filters) {
        const visibleKeys = new Set(GenerateInvoicesTableColumns.value.map(c => c.key))
        for (let i = table.filters.length - 1; i >= 0; i--) {
          if (!visibleKeys.has(table.filters[i].key.replace(/_(gt|lt)$/, ''))) table.filters.splice(i, 1)
        }
      }
      await handleRefresh()
    })

    // Drag-to-resize column headers, same as the Invoicing tab's tables.
    // Re-run on every update: the table only exists once the drafts have
    // loaded (v-else), and the toggle adds/removes header cells. Already
    // wired headers are skipped (see enableColumnResize).
    onUpdated(() => nextTick(() => enableColumnResize(instance.proxy.$el)))

    // ── Approval ─────────────────────────────────────────────────────────

    // Matches a bulk-approve result/error entry back to the original draft
    // group it came from — Lincoln's results[]/errors[] entries don't carry
    // enough fields alone (results[] has no currency_type/billing_month/
    // billing_year; errors[] nests the original sent item under `.item`),
    // so both are matched via this same composite key instead of raw
    // array position (which partial failures would throw off).
    const isSameDraftGroup = (a, b) =>
      a.billing_type === b.billing_type &&
      a.tpa_company_id === b.tpa_company_id &&
      a.underwriter_company_id === b.underwriter_company_id &&
      (a.syndicate_number || null) === (b.syndicate_number || null) &&
      a.currency_type === b.currency_type &&
      a.billing_month === b.billing_month &&
      a.billing_year === b.billing_year

    const approveSelected = async () => {
      if (!selectedDraftObjects.value.length) return

      const blocked = selectedDraftObjects.value.filter(d => draftMissingRequirements(d).length > 0)
      if (blocked.length) {
        const reasons = new Set()
        blocked.forEach(d => draftMissingRequirements(d).forEach(r => reasons.add(r)))
        proxy.$bvToast && proxy.$bvToast.toast(
          `${blocked.length} selected invoice(s) cannot be approved — missing ${[...reasons].join(', ')}. Add these on the TPA's company profile / billing contacts first.`,
          { title: 'Missing required info', variant: 'danger', autoHideDelay: 5000 }
        )
        return
      }

      approving.value = true
      // Snapshot before clearSelection() runs below — selectedDraftObjects
      // is a computed off modelValue, so it goes empty the moment the
      // selection is cleared.
      const targetDrafts = selectedDraftObjects.value
      try {
        const invoices = targetDrafts.map(draft => ({
          tpa_company_id: draft.tpa_company_id,
          underwriter_company_id: draft.underwriter_company_id,
          currency_type: draft.currency_type,
          billing_month: draft.billing_month,
          billing_year: draft.billing_year,
          // Identifies WHICH group is being approved. A Market group and a
          // LIMOSS group can share tpa/underwriter/currency/period, so without
          // this both sets of charges land on one invoice.
          billing_type: draft.billing_type,
          // A LIMOSS group is split by syndicate too, so approving one
          // syndicate's draft only links that syndicate's own charges.
          // Always null for Market.
          syndicate_number: draft.syndicate_number,
          dispatch_mode: 'auto',
          invoice_reference: exportState.generateRef(draft),
          fv_project_id: draft.tpa_filevine_id,
        }))

        // Lincoln approves items serially (one draft-charges query + insert
        // each), and the whole request has to finish inside LimeIQ's 30s
        // proxy/gunicorn timeout — a large selection sent in one go timed
        // out, the FE never saw the created invoices, and so never
        // generated/uploaded their PDF/XLS. Sending small batches keeps each
        // request well inside that window, and uploading each batch's
        // documents straight away means a later batch failing can't strand
        // invoices that were already approved.
        let approvedCount = 0
        let failedBatches = 0
        for (let start = 0; start < invoices.length; start += BULK_APPROVE_BATCH_SIZE) {
          const batchInvoices = invoices.slice(start, start + BULK_APPROVE_BATCH_SIZE)
          const batchDrafts = targetDrafts.slice(start, start + BULK_APPROVE_BATCH_SIZE)

          let results, errors
          try {
            const resp = await InvoicingService.bulkApproveInvoices(batchInvoices)
            // Lincoln's own {status, results, errors} body gets wrapped again
            // by the proxy layer into {status, data: {...}} — same envelope
            // every other call site in this app unwraps via resp.data.data.
            ;({ results, errors } = (resp.data && resp.data.data) || {})
          } catch (e) {
            console.error('Approval batch failed:', batchInvoices, e)
            failedBatches += 1
            continue
          }

          if (errors && errors.length) {
            console.warn('Approval errors:', errors)
          }

          if (results && results.length) {
            approvedCount += results.length
            const failedItems = (errors || []).map(e => e.item)
            const succeededDrafts = batchDrafts.filter((draft, i) =>
              !failedItems.some(failed => isSameDraftGroup(failed, batchInvoices[i])))
            if (succeededDrafts.length === results.length) {
              const newlyApproved = results.map((result, i) => ({ ...succeededDrafts[i], ...result }))
              await exportState.generateAndUploadDocuments(newlyApproved)
            }
          }
        }

        if (approvedCount) {
          proxy.$bvToast && proxy.$bvToast.toast(
            `${approvedCount} invoice(s) approved successfully.`,
            { title: 'Approved', variant: 'success', autoHideDelay: 3000 }
          )
        }
        if (failedBatches) {
          proxy.$bvToast && proxy.$bvToast.toast(
            'Some invoices could not be approved. Refresh and check the remaining drafts before retrying.',
            { title: 'Error', variant: 'danger', autoHideDelay: 6000 }
          )
        }

        clearSelection()
        await loadDrafts()
        await handleRefresh()
        emit('changed')
      } catch (e) {
        console.error('Approval failed:', e)
        proxy.$bvToast && proxy.$bvToast.toast(
          'Failed to approve invoices. Please try again.',
          { title: 'Error', variant: 'danger', autoHideDelay: 4000 }
        )
      } finally {
        approving.value = false
      }
    }

    const tableTbodyTrClass = () => ''

    loadDrafts()

    return {
      exportState,
      GenerateInvoicesTableColumns,

      drafts,
      loadingDrafts,
      draftError,
      total,

      modelValue,
      dataTableRef,
      billingTypeOptions,
      billingTypeFilter,
      billingTypeModel,
      selectedDraftObjects,
      hasMissingRequirements,
      missingRequirementsSummary,
      firstAddressLine,
      truncateFilevine,

      approving,

      itemsProvider,
      tableTbodyTrClass,
      handleRefresh,
      clearSelection,

      approveSelected,

      loadDrafts,
    }
  },
})
</script>

<style scoped>
.gi-root { padding: 0; }

.gi-root >>> .table {
  font-size: 11px;
  table-layout: fixed;
}
.gi-root >>> .table th:first-child,
.gi-root >>> .table td:first-child {
  width: 28px !important;
  min-width: 28px !important;
  max-width: 28px !important;
  padding-left: 4px !important;
  padding-right: 4px !important;
}
.gi-root >>> .table td > div {
  padding: 8px 10px!important;
}
.gi-root >>> .table td:first-child > div {
  padding: 0!important;
}
.gi-root >>> .table th,
.gi-root >>> .table td {
  font-size: 11px!important;
  white-space: normal;
  word-break: break-word;
  vertical-align: middle;
}
.gi-root >>> .table thead th {
  font-size: 10px!important;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 8px 6px;
}
.gi-root >>> .table .custom-checkbox {
  margin: 0 !important;
  display: inline-flex;
  align-items: center;
}
.gi-root >>> .card .card-body {
    padding: 0 1.5rem!important;
}
.gi-root >>> .container {
    padding: 0!important;
    max-width: initial!important;
}

.gi-table-label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.06em; color: #8898aa; margin-bottom: 8px;
}

.gi-billing-toggle { vertical-align: middle; }

.gi-billing-toggle >>> .btn { font-size: 11px; padding: 3px 12px; }

.gi-action-bar {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px; flex-wrap: wrap; gap: 8px;
}
.gi-action-bar__count { font-size: 13px; font-weight: 600; color: #32325d; }
.gi-action-bar__buttons { display: flex; gap: 6px; }

.gi-approve-btn { display: inline-flex; align-items: center; }
/* The (i) icon lives inside the approve button but @click.stop keeps a
   click on it from also bubbling up and firing the approve action. */
.gi-info-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 50%;
  cursor: help; font-size: 10px;
}
.gi-info-icon:hover, .gi-info-icon:focus { background: rgba(255, 255, 255, 0.25); outline: none; }

.gi-progress-bar {
  background: #fff; border: 1px solid #e9ecef; border-radius: 6px;
  padding: 10px 16px; margin-bottom: 12px;
}
.gi-progress-bar__label {
  font-size: 12px; color: #32325d; font-weight: 600; margin-bottom: 6px;
}
.gi-progress-bar__track {
  width: 100%; height: 8px; border-radius: 4px;
  background: #e9ecef; overflow: hidden;
}
.gi-progress-bar__fill {
  height: 100%; background: #1c4a50; border-radius: 4px;
  transition: width 0.2s ease;
}

.gi-ref { color: #1c4a50; font-size: 10px; font-family: monospace; }
.gi-num { display: inline-block; font-size: 11px; }
.gi-amount { font-weight: 600; color: #1c4a50; font-size: 11px; }
.gi-contact { font-size: 10px; color: #525f7f; white-space: nowrap; text-wrap: inherit; }

/* Address & FileVine cells only — keep the (already JS-truncated) value on
   a single line so the ellipsis sits right after the text instead of
   wrapping. Not applied to the billing-contact cell, which needs its
   "+N" more-link to stay visible. */
.gi-truncate-cell {
  display: inline-block; max-width: 100%; vertical-align: bottom;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.gi-contact-more {
  color: #1c4a50; font-weight: 600; margin-left: 4px;
  text-decoration: none; cursor: pointer;
}
.gi-contact-more:hover { text-decoration: underline; }
.gi-variance-pct { font-size: 9px; color: #8898aa; margin-left: 2px; }
.gi-alert-flag { color: #e67e22; font-weight: 700; margin-left: 2px; font-size: 10px; }
.gi-td--alert { color: #e67e22; font-weight: 600; }

.gi-status-badge {
  display: inline-block; padding: 1px 6px; border-radius: 10px;
  font-size: 9px; font-weight: 600; background: #e9ecef; color: #525f7f;
  white-space: nowrap;
}
.gi-status-badge--draft { background: #e9ecef; color: #525f7f; }
.gi-status-badge--updated { background: #fff3cd; color: #856404; }

.gi-preview-btn { font-size: 10px; padding: 2px 7px; margin-left: 3px; }

.gi-loading, .gi-empty { padding: 40px; text-align: center; color: #8898aa; font-size: 13px; }
.gi-error { padding: 16px; text-align: center; color: #dc3545; font-size: 13px; }

.bcm-root { position: relative; padding: 4px 0 0; }
.bcm-close {
  position: absolute; top: -8px; right: -8px;
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 50%; border: none;
  background: transparent; color: #8898aa; font-size: 1.4rem;
  cursor: pointer; padding: 0; line-height: 1;
  transition: background 0.15s, color 0.15s;
}
.bcm-close:hover { background: #f0f0f0; color: #32325d; }

.bcm-header { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 16px; }
.bcm-header__icon-wrap {
  display: flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; border-radius: 50%;
  background: #e8f4f0; flex-shrink: 0;
}
.bcm-header__icon { font-size: 1.2rem; color: #1c4a50; }
.bcm-header__title { font-size: 0.95rem; font-weight: 700; color: #32325d; margin: 0 0 2px; }
.bcm-header__sub { font-size: 0.8rem; color: #525f7f; margin: 0; }

.bcm-list { list-style: none; margin: 0; padding: 0; }
.bcm-list__item {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; border-radius: 6px; font-size: 13px; color: #32325d;
  border-bottom: 1px solid #f1f3f5;
}
.bcm-list__item:last-child { border-bottom: none; }
.bcm-list__icon { color: #1c4a50; font-size: 12px; flex-shrink: 0; }
</style>
